/** Notes — private notes, team knowledge (a note shared with a team) and reminders. Same editor, same search. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import DOMPurify from "dompurify";
import { AlarmClock as Alarm, Bold, Check, CheckSquare, ChevronRight, Folder, Heading2, Italic, Link2, List, ListOrdered, Lock, Pin, Plus, Quote, Search, Trash2, Underline, Users } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, del, patch, post } from "../lib/api";
import { Card, Empty, ErrorNote, PageHeader, fmtDate } from "../components/ui";
import { RichEditor } from "../components/RichEditor";
import { SubProgress } from "../components/work";
import { LinkedWork, SendToButton } from "../components/SendToApp";

interface Note {
  id: string; kind: "note" | "reminder"; title: string; body: string; snippet: string; folder: string; tags: string[]; pinned: boolean;
  remindAt: string | null; doneAt: string | null; workItemId: string | null; workItemSubtasks?: { total: number; done: number } | null; team: { id: string; name: string | null } | null; ownerName: string | null; updatedAt: string;
}
interface FolderRow { folder: string; n: number; team: string | null }

const useNotes = (params: URLSearchParams) => useQuery({ queryKey: ["notes", params.toString()], queryFn: () => api<{ items: Note[] }>(`/api/notes?${params}`) });
const when = (iso: string) => new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function Capture() {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const m = useMutation({
    mutationFn: (t: string) => post<Note>("/api/notes", { capture: t }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["notes"] }); void qc.invalidateQueries({ queryKey: ["note-folders"] }); },
    onError: (_e, t) => setText((cur) => cur || t), // give the words back if saving failed
  });
  const submit = () => { const t = text.trim(); if (!t) return; setText(""); m.mutate(t); };
  return (
    <div>
      <div className="flex items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--panel)] px-3 py-2 focus-within:border-brand-400">
        <Plus size={16} className="muted" />
        <input className="min-w-0 flex-1 bg-transparent py-1 text-sm outline-none" placeholder="Jot something… or “remind me to call the accountant tomorrow 9am”" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }} aria-label="New note" />
      </div>
      <ErrorNote error={m.error} />
    </div>
  );
}

function Reminders() {
  const qc = useQueryClient();
  const q = useNotes(new URLSearchParams({ kind: "reminder" }));
  const act = useMutation({
    mutationFn: (v: { id: string; snooze?: string }) => post(`/api/notes/${v.id}/done`, v.snooze ? { snoozeUntil: v.snooze } : { done: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes"] }),
  });
  const items = q.data?.items ?? [];
  if (!items.length) return null;
  const tomorrow9 = () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d.toISOString(); };
  return (
    <Card title="Reminders" icon={<Alarm size={16} />} count={items.length} flush>
      <ul className="px-2 pb-1">
        {items.map((r) => {
          const due = r.remindAt && new Date(r.remindAt) <= new Date();
          return (
            <li key={r.id} className="flex items-start gap-2 rounded-lg px-3 py-2 text-sm hover:bg-black/[0.03]">
              <button aria-label="Done" className="mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded border-[1.5px] border-[var(--muted)] hover:border-emerald-600" onClick={() => act.mutate({ id: r.id })} />
              <span className="min-w-0 flex-1">
                <span className="block break-words">{r.title}</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-1">
                  <span className={clsx("text-xs", due ? "font-semibold text-amber-700" : "muted")}>{r.remindAt ? when(r.remindAt) : ""}</span>
                  <button className="btn btn-ghost btn-sm !min-h-0 !px-1.5 !py-0 text-xs" title="Snooze 1 hour" onClick={() => act.mutate({ id: r.id, snooze: new Date(Date.now() + 3600_000).toISOString() })}>+1h</button>
                  <button className="btn btn-ghost btn-sm !min-h-0 !px-1.5 !py-0 text-xs" title="Tomorrow 09:00" onClick={() => act.mutate({ id: r.id, snooze: tomorrow9() })}>Tomorrow</button>
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function FolderTree({ value, onChange }: { value: { folder: string; scope: string; team?: string }; onChange: (v: { folder: string; scope: string; team?: string }) => void }) {
  const q = useQuery({ queryKey: ["note-folders"], queryFn: () => api<{ items: FolderRow[] }>("/api/notes/folders") });
  const groups = useMemo(() => {
    const g = new Map<string, Map<string, number>>();
    for (const r of q.data?.items ?? []) {
      const key = r.team ?? "My notes";
      const m = g.get(key) ?? new Map<string, number>();
      // count into every level of the path so parents show totals
      const parts = r.folder ? r.folder.split("/") : [];
      m.set("", (m.get("") ?? 0) + r.n);
      parts.forEach((_, i) => { const p = parts.slice(0, i + 1).join("/"); m.set(p, (m.get(p) ?? 0) + r.n); });
      g.set(key, m);
    }
    return [...g.entries()].sort(([a], [b]) => (a === "My notes" ? -1 : b === "My notes" ? 1 : a.localeCompare(b)));
  }, [q.data]);
  const item = (label: string, active: boolean, n: number, depth: number, onClick: () => void, icon = <Folder size={14} />) => (
    <button className={clsx("flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm", active ? "bg-brand-50 font-semibold text-brand-700" : "hover:bg-black/[0.04]")} style={{ paddingLeft: 8 + depth * 14 }} onClick={onClick}>
      {icon}<span className="min-w-0 flex-1 truncate">{label}</span><span className="muted text-xs">{n}</span>
    </button>
  );
  return (
    <div className="space-y-3">
      {item("All notes", value.scope === "all" && !value.folder && !value.team, (q.data?.items ?? []).reduce((a, r) => a + r.n, 0), 0, () => onChange({ folder: "", scope: "all" }), <ChevronRight size={14} />)}
      {groups.map(([group, m]) => (
        <div key={group}>
          <div className="muted flex items-center gap-1.5 px-2 pb-1 text-[11px] font-bold uppercase tracking-wide">{group === "My notes" ? <Lock size={11} /> : <Users size={11} />} {group}</div>
          {[...m.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([path, n]) => {
            const depth = path ? path.split("/").length : 0;
            const scope = group === "My notes" ? "mine" : "shared";
            const team = group === "My notes" ? undefined : group;
            return <div key={path}>{item(path ? path.split("/").at(-1)! : group === "My notes" ? "Everything private" : "Everything shared", value.folder === path && value.scope === scope && value.team === team, n, depth, () => onChange({ folder: path, scope, team }))}</div>;
          })}
        </div>
      ))}
    </div>
  );
}

function Editor({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const n = useQuery({ queryKey: ["note", id], queryFn: () => api<Note>(`/api/notes/${id}`) });
  const teams = useQuery({ queryKey: ["note-teams"], queryFn: () => api<{ items: Array<{ id: string; name: string }> }>("/api/notes/teams") });
  const [saved, setSaved] = useState<"idle" | "saving" | "saved">("idle");
  const save = useMutation({
    mutationFn: (b: Record<string, unknown>) => patch<Note>(`/api/notes/${id}`, b),
    onMutate: () => setSaved("saving"),
    onSuccess: () => { setSaved("saved"); void qc.invalidateQueries({ queryKey: ["notes"] }); void qc.invalidateQueries({ queryKey: ["note-folders"] }); },
  });
  const toTask = useMutation({ mutationFn: () => post<{ workItemId: string }>(`/api/notes/${id}/to-task`, {}), onSuccess: (r) => void navigate({ to: "/work/$id", params: { id: r.workItemId } }) });
  const remove = useMutation({ mutationFn: () => del(`/api/notes/${id}`), onSuccess: () => { void qc.invalidateQueries({ queryKey: ["notes"] }); onClose(); } });
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  if (!n.data) return <Card><Empty>{n.isLoading ? "Loading…" : "Note not found."}</Empty></Card>;
  const d = n.data;
  return (
    <Card flush>
      <div className="space-y-3 p-4">
        <input className="w-full bg-transparent text-xl font-bold outline-none" defaultValue={d.title} placeholder="Title" onBlur={(e) => e.target.value !== d.title && save.mutate({ title: e.target.value })} />
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <label className="flex items-center gap-1"><Folder size={13} className="muted" /><input className="input !h-7 !w-40 !py-0 text-xs" defaultValue={d.folder} placeholder="Folder, e.g. IFT/Tenders" onBlur={(e) => e.target.value !== d.folder && save.mutate({ folder: e.target.value })} /></label>
          <input className="input !h-7 !w-40 !py-0 text-xs" defaultValue={d.tags.join(", ")} placeholder="Tags, comma separated" onBlur={(e) => save.mutate({ tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} />
          <select className="input !h-7 !w-auto !py-0 text-xs" value={d.team?.id ?? ""} onChange={(e) => save.mutate({ teamId: e.target.value || null })} aria-label="Shared with">
            <option value="">Private — only me</option>
            {teams.data?.items.map((t) => <option key={t.id} value={t.id}>Shared with {t.name}</option>)}
          </select>
          <span className="muted ml-auto">{saved === "saving" ? "Saving…" : saved === "saved" ? "Saved" : d.ownerName && d.team ? `By ${d.ownerName} · ${fmtDate(d.updatedAt)}` : fmtDate(d.updatedAt)}</span>
        </div>
        <RichEditor key={d.id} value={d.body} title={d.title} minHeight={360} placeholder="Write… (paste or drop pictures, add tables, colours, check lists)"
          onChange={(html) => { clearTimeout(timer.current); timer.current = setTimeout(() => save.mutate({ body: html }), 800); }} />
        <ErrorNote error={save.error ?? toTask.error ?? remove.error} />
        <div className="mt-4"><LinkedWork source={{ noteId: id }} /></div>
      </div>
      <div className="flex flex-wrap gap-2 border-t border-[var(--line)] px-4 py-3">
        <button className={clsx("btn btn-sm", d.pinned && "!border-amber-300 !bg-amber-50")} onClick={() => save.mutate({ pinned: !d.pinned })}><Pin size={14} /> {d.pinned ? "Pinned" : "Pin"}</button>
        {d.workItemId ? <button className="btn btn-sm" onClick={() => void navigate({ to: "/work/$id", params: { id: d.workItemId! } })}><CheckSquare size={14} /> Open task{d.workItemSubtasks && <span className="ml-1"><SubProgress s={d.workItemSubtasks} /></span>}</button> : <button className="btn btn-sm" onClick={() => toTask.mutate()}><CheckSquare size={14} /> Turn into task</button>}
        <SendToButton source={{ noteId: id }} />
        <span className="flex-1" />
        <button className="btn btn-sm btn-danger" onClick={() => confirm("Delete this note?") && remove.mutate()}><Trash2 size={14} /> Delete</button>
        <button className="btn btn-sm" onClick={onClose}><Check size={14} /> Done</button>
      </div>
    </Card>
  );
}

export function NotesPage() {
  const qc = useQueryClient();
  const [sel, setSel] = useState<{ folder: string; scope: string; team?: string }>({ folder: "", scope: "all" });
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(() => new URLSearchParams(location.search).get("note"));
  const params = new URLSearchParams({ kind: "note", scope: sel.scope === "all" ? "all" : sel.scope, ...(sel.folder ? { folder: sel.folder } : {}), ...(q ? { q } : {}) });
  const notes = useNotes(params);
  const items = (notes.data?.items ?? []).filter((n) => !sel.team || n.team?.name === sel.team);
  const create = useMutation({
    mutationFn: () => post<Note>("/api/notes", { title: "Untitled note", folder: sel.folder }),
    onSuccess: (n) => { void qc.invalidateQueries({ queryKey: ["notes"] }); setOpen(n.id); },
  });
  return (
    <div className="space-y-4">
      <PageHeader title="Notes" subtitle="Your private notes, your teams' shared knowledge, and reminders — one place, one search." actions={<button className="btn btn-primary" onClick={() => create.mutate()}><Plus size={16} /> New note</button>} />
      <div className="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)] xl:grid-cols-[16rem_minmax(0,22rem)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Capture />
          <Reminders />
          <Card><FolderTree value={sel} onChange={(v) => { setSel(v); }} /></Card>
        </div>
        <div className={clsx("space-y-3", open && "hidden xl:block")}>
          <div className="relative"><Search size={15} className="muted absolute left-3 top-[11px]" /><input className="input !pl-9" placeholder="Search notes, tags…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          {notes.isLoading ? <Empty>Loading…</Empty> : items.length ? (
            <ul className="space-y-2">
              {items.map((n) => (
                <li key={n.id}>
                  <button onClick={() => setOpen(n.id)} className={clsx("w-full rounded-xl border bg-[var(--panel)] p-3 text-left hover:border-brand-300", open === n.id ? "border-brand-400" : "border-[var(--line)]")}>
                    <div className="flex items-center gap-2">
                      {n.pinned && <Pin size={13} className="text-amber-600" />}
                      <span className="min-w-0 flex-1 truncate font-semibold">{n.title || "Untitled"}</span>
                      <SubProgress s={n.workItemSubtasks} />
                      {n.team ? <span className="chip text-xs"><Users size={11} /> {n.team.name}</span> : <Lock size={12} className="muted" />}
                    </div>
                    {n.snippet && <p className="muted mt-1 line-clamp-2 text-xs">{n.snippet}</p>}
                    <div className="muted mt-1.5 flex flex-wrap gap-2 text-[11px]">{n.folder && <span><Folder size={11} className="inline" /> {n.folder}</span>}{n.tags.map((t) => <span key={t}>#{t}</span>)}<span className="ml-auto">{fmtDate(n.updatedAt)}</span></div>
                  </button>
                </li>
              ))}
            </ul>
          ) : <Card><Empty>No notes here yet. Jot one in the box on the left, or press “New note”.</Empty></Card>}
        </div>
        {open ? <div className="lg:col-span-1 xl:col-span-1"><Editor key={open} id={open} onClose={() => setOpen(null)} /></div> : <div className="hidden xl:block"><Card><Empty>Pick a note, or start a new one.</Empty></Card></div>}
      </div>
    </div>
  );
}
